import React, { useMemo } from 'react';
import { Image, Minus, Plus, X } from 'react-feather';
import { LightgalleryItem, LightgalleryProvider } from 'react-lightgallery';
import { useNavigate } from 'react-router-dom';
import { PurchaseOrderStatus } from '../enums/purchase-order-status';
import { ProductImage } from '../interfaces/product-image';
import { PurchaseOrder } from '../interfaces/purchase-order';
import { formatNumber } from '../services/helpers';
import myToastr from '../services/toastr';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { addProductUnit, createPurchaseOrder, ProductInShoppingCart, purchaseOrderSelector, removeProduct, removeProductUnit, updatePurchaseOrder } from '../store/purchase-order-slice';

interface Props {
  onClose: () => void;
}

const ShoppingCartPurchaseOrder = ({ onClose }: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { purchaseOrder, products, total, requesting, destinationStoreId } = useAppSelector(purchaseOrderSelector);
  const isValid: boolean = useMemo(() => purchaseOrder !== null || (destinationStoreId !== null && destinationStoreId > 0), [purchaseOrder, destinationStoreId]);

  const save = async () => {
    if (purchaseOrder) {
      const result = await dispatch(updatePurchaseOrder(PurchaseOrderStatus.Draft));
      if (!result.payload) {
        return;
      }
      myToastr.success('Órden de compra actualizada');
    } else {
      const result = await dispatch(createPurchaseOrder());
      if (!result.payload) {
        return;
      }
      myToastr.success('Órden de compra guardada');
    }
    navigate('/ordenes-compra');
  };

  const checkout = async () => {
    if (purchaseOrder) {
      const result = await dispatch(updatePurchaseOrder(PurchaseOrderStatus.Draft));
      if (!result.payload) {
        return;
      }
      const updatedPurchaseOrder: PurchaseOrder = result.payload as PurchaseOrder;
      navigate(`/orden-compra/${updatedPurchaseOrder.id}`);
    } else {
      const result = await dispatch(createPurchaseOrder());
      if (!result.payload) {
        return;
      }
      const newPurchaseOrder: PurchaseOrder = result.payload as PurchaseOrder;
      navigate(`/orden-compra/${newPurchaseOrder.id}`);
    }
    onClose();
  };

  return (
    <div className="shopping-cart-purchase-order d-flex flex-column">
      <div className="d-flex flex-row justify-content-between">
        <h6>Carrito</h6>
        <X
          onClick={() => {
            if (requesting) {
              return;
            }
            onClose();
          }}
          className="clickable"
          size={16}
          color="black"
        />
      </div>
      <div className="d-flex flex-column">
        {products.length === 0 ? (
          <span className="no-products">No hay productos en el carrito</span>
        ) : (
          <React.Fragment>
            <div className="container-products">
              {products.map((productInShoppingCart: ProductInShoppingCart) => {
                let productName: string = productInShoppingCart.product.name;
                if (productInShoppingCart.product.name.length > 30) {
                  productName = productInShoppingCart.product.name.substring(0, 50) + '...';
                }
                const price = productInShoppingCart.discountPercentage ? productInShoppingCart.retailPrice * (1 - productInShoppingCart.discountPercentage / 100) : productInShoppingCart.retailPrice;
                return (
                  <div key={productInShoppingCart.product.id} className="d-flex flex-row align-items-center container-product">
                    <div className="image-container">
                      {productInShoppingCart.product.images.length > 0 ? (
                        <div className="me-2">
                          <LightgalleryProvider>
                            {productInShoppingCart.product.images.map((productImage: ProductImage, index: number) => {
                              return (
                                <LightgalleryItem key={productImage.id} group="group-product-images" src={process.env.REACT_APP_PUBLIC_URL + productImage.path}>
                                  {index === 0 && <img src={process.env.REACT_APP_PUBLIC_URL + productImage.path} alt="" className="img-thumbnail" />}
                                </LightgalleryItem>
                              );
                            })}
                          </LightgalleryProvider>
                        </div>
                      ) : (
                        <Image size={38} className="img-thumbnail my-1 me-2" color="#D3D3D3" />
                      )}
                    </div>
                    <div className="d-flex flex-column">
                      <span className="product-name">{productName}</span>
                      <span className="product-retail-price">{formatNumber(price)}€</span>
                      <div className="d-flex flex-row">
                        <div className="d-flex flex-row align-items-center justify-content-between container-units">
                          <Minus onClick={() => dispatch(removeProductUnit(productInShoppingCart.product.id))} className="clickable" size={12} color="black" />
                          <span className="product-units noselect">{productInShoppingCart.units}</span>
                          <Plus onClick={() => dispatch(addProductUnit(productInShoppingCart.product.id))} className="clickable" size={12} color="black" />
                        </div>
                        <span onClick={() => dispatch(removeProduct(productInShoppingCart.product.id))} className="delete noselect">
                          Eliminar
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="d-flex flex-row justify-content-between container-total">
              <span className="total">Subtotal</span>
              <span className="total-price">{formatNumber(total)}€</span>
            </div>
            <div className="d-flex flex-row justify-content-between container-buttons">
              <button onClick={save} disabled={!isValid || requesting} className="save">
                Guardar
              </button>
              <button onClick={checkout} disabled={!isValid || requesting} className="finalize">
                Checkout
              </button>
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

export default ShoppingCartPurchaseOrder;
